import React, { useEffect } from 'react';
import { Render } from '@puckeditor/core';

import { CMS_MODEL_ID } from 'src/cms/standalone-page/constants';
import { standalonePageRenderConfig } from 'src/cms/standalone-page-renderer/render-config';
import { useCmsResolvedContent } from 'src/cms/use-cms-resolved-content';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUI from 'src/hooks/use-ui';

import { HOME_URL_PATH } from './home-cms-constants';
import { useHomeMenuContext } from './home-menu-context';

type HomeCmsContentProps = {
  /** Rendered when no CMS override is published/resolved for the homepage. */
  fallback: React.ReactNode;
};

/**
 * Resolves and renders the homepage's CMS content for the resolved enterprise +
 * store, feeding the geolocation-resolved `dispensaryId` into the CMS resolve
 * call for targeting (ECOMM-11826).
 *
 * Falls back to the generic marketplace homepage while loading and whenever no
 * CMS page has been published/resolved for this enterprise/store, so enabling the
 * resolution never blanks the homepage.
 */
export function HomeCmsContent({ fallback }: HomeCmsContentProps): JSX.Element {
  const menuContext = useHomeMenuContext();
  const enterpriseId = menuContext?.enterpriseId;
  const dispensaryId = menuContext?.dispensaryId;

  // Apply the resolved store's theme (brand colors/fonts/`--dcm-*` vars) to the homepage. The
  // global ThemeProvider (consumer-shell) themes off `UI.enterpriseDispensary`; the homepage isn't
  // a cName route, so we load the dispensary by its resolved id and publish it here -- the same
  // mechanism the enterprise enrollment pages use. Without this the CMS page renders with the
  // default, unbranded theme. The dispensary is SSR-prefetched (get-home-server-side-props) so it
  // resolves from cache on first paint. See ECOMM-11826.
  const UI = useUI();
  const { dispensary: themeDispensary } = useDispensary(dispensaryId ?? undefined);
  useEffect(() => {
    if (themeDispensary) {
      UI.setEnterpriseDispensary(themeDispensary);
    }
    return () => {
      UI.setEnterpriseDispensary(null);
    };
  }, [UI, themeDispensary]);

  const { data: pageData, loading: contentLoading } = useCmsResolvedContent(CMS_MODEL_ID, {
    enterpriseId,
    dispensaryId,
    urlPath: HOME_URL_PATH,
  });

  if (contentLoading || !pageData) {
    return <>{fallback}</>;
  }

  return <Render config={standalonePageRenderConfig} data={pageData} />;
}
